import * as Api from './api'
import { action, makeObservable, observable, runInAction } from 'mobx'

export interface Identity {
  username: string
  role: string
  Telephone: string
  settings: null | UserSettings
  banner: BannerDef
}

export interface BannerDef {
  URL: string
  Image: string
}
export interface UserSettings {
  zoom: number
  latitude: number
  longitude: number
  showClusters: boolean
  mapFilters: {
    machines: boolean
    branches: boolean
    sites: boolean
    plants: boolean
    vehicles: boolean
  }
}

export interface Account {
  user: Identity | null | undefined
  isSessionAboutToExpire: boolean
  isLoggedIn: boolean
  sessionExpired: boolean
  retrieved: boolean
  lastRequestTimestamp: number | null
}

class AccountStore {
  user: Identity | null | undefined = null
  lastRequestTimestamp: number | null = null
  isSessionAboutToExpire = false
  sessionExpired = false
  isLoggedIn = false
  retrieved = false

  EXPIRATION_DIALOG_TIMEOUT = 27 * 1000 * 60
  EXPIRATION_TIMEOUT = 29 * 1000 * 60

  constructor() {
    makeObservable(this, {
      user: observable,
      lastRequestTimestamp: observable,
      isSessionAboutToExpire: observable,
      isLoggedIn: observable,
      retrieved: observable,
      sessionExpired: observable,
      login: action,
      logout: action,
      getProfile: action,
      updateSettings: action,
      setLastRequestTimestamp: action,
      resetLastRequestTimeout: action,
      checkSessionExpiration: action
    })
    this.checkSessionExpiration = this.checkSessionExpiration.bind(this)
    setInterval(this.checkSessionExpiration, 1000)
  }

  checkSessionExpiration() {
    if (
      this.lastRequestTimestamp &&
      Date.now() - this.lastRequestTimestamp > this.EXPIRATION_TIMEOUT
    ) {
      this.sessionExpired = true
      this.logout()
    } else if (
      this.lastRequestTimestamp &&
      Date.now() - this.lastRequestTimestamp > this.EXPIRATION_DIALOG_TIMEOUT
    ) {
      this.isSessionAboutToExpire = true
    } else if (this.isSessionAboutToExpire) {
      this.isSessionAboutToExpire = false
    }
  }

  setLastRequestTimestamp() {
    if (!this.isLoggedIn) {
      return
    }
    this.lastRequestTimestamp = Date.now()
  }

  resetLastRequestTimeout() {
    this.lastRequestTimestamp = null
  }

  login(username: string, password: string) {
    this.isSessionAboutToExpire = false
    this.retrieved = true
    this.sessionExpired = false
    this.isLoggedIn = false
    this.lastRequestTimestamp = null
    return Api.login(username, password)
      .then(
        action(() => {
          this.getProfile()
          this.isLoggedIn = true
          this.lastRequestTimestamp = Date.now()
        })
      )
      .catch((error) => {
        console.log(error)
      })
  }

  logout() {
    Api.logout()
      .then(
        action(() => {
          this.user = undefined
          this.isSessionAboutToExpire = false
          this.retrieved = false
          this.isLoggedIn = false
          this.lastRequestTimestamp = null
          this.resetLastRequestTimeout()
        })
      )
      .catch((error) => {
        console.log(error)
      })
  }

  getProfile() {
    return Api.getProfile()
      .then(
        action(async (identity) => {
          const splashTimeout: number = import.meta.env.REACT_APP_SPLASH_TIMEOUT
            ? +import.meta.env.REACT_APP_SPLASH_TIMEOUT
            : 1000
          setTimeout(() => {
            runInAction(() => {
              this.user = identity
              this.isSessionAboutToExpire = false
              this.retrieved = true
              this.sessionExpired = false
              this.isLoggedIn = true
              this.lastRequestTimestamp = Date.now()
            })
          }, splashTimeout)
        })
      )
      .catch(
        action(async () => {
          this.retrieved = true
          setTimeout(() => {
            runInAction(() => {
              this.isSessionAboutToExpire = false
              this.retrieved = true
              // eslint-disable-next-line no-self-assign
              this.sessionExpired = this.sessionExpired
              this.isLoggedIn = false
              this.lastRequestTimestamp = null
            })
          }, 1000)
        })
      )
  }

  updateSettings(settings: UserSettings) {
    if (this.user) {
      this.user.settings = settings
    }

    Api.updateSettings(settings).then(
      action((identity: Identity) => {
        this.user = identity
      })
    )
  }

  isAdmin() {
    return this.user && (this.user.role === 'admin' || this.user.role === 'superuser')
  }
}

export default new AccountStore()
